import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import {
  RouteGroup,
  useGetInboundTransport,
  useGetInboundTransportTotals,
  useInboundTransportYearStatus,
} from "@/hooks/useTransport";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { BranchesOutlined, DeleteOutlined, ExportOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { useState } from "react";
import DeleteInboundTransportAction from "../DeleteInboundTransportAction";
import ExportInboundTransportAction from "../ExportInboundTransportAction";
import InboundRouteWizard from "../RouteWizard/InboundRouteWizard";
import "../Transport.scss";
import { transportUploadFilters } from "../constants";
import InboundTransportButtonGroup from "./InboundTransportButtonGroup";

const rowLink = (record: RouteGroup) => {
  return `/transport/inbound/${record.departure_id}/${record.arrival_id}/${encodeURIComponent(
    record.transport_category || ""
  )}`;
};

export default function InboundTransport() {
  const translation = useTranslation();

  const { year } = useDashboardYear();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  useTrackOnLoad("Inbound Transport Viewed");

  const columns: Column<RouteGroup>[] = [
    {
      key: "departure_name",
      label: translation.transport.departureFacility,
      searchable: true,
      minWidth: "210px",
      noLink: true,
      renderer: (record) => (
        <CrossReference
          to={"/facilities/" + record.departure_id}
          secondaryTo={rowLink(record)}
        >
          {record.departure_name}
        </CrossReference>
      ),
    },
    {
      key: "arrival_name",
      label: translation.transport.arrivalFacility,
      searchable: true,
      minWidth: "190px",
      noLink: true,
      renderer: (record) => (
        <CrossReference
          to={"/facilities/" + record.arrival_id}
          secondaryTo={rowLink(record)}
        >
          {record.arrival_name}
        </CrossReference>
      ),
    },
    {
      key: "transport_category",
      minWidth: "230px",
      label: translation.transport.transportCategory,
      searchable: true,
    },
    {
      key: "transport_mode",
      minWidth: "200px",
      label: translation.transport.modeOfTransport,
    },
    {
      key: "orders_on_route",
      minWidth: "200px",
      label: translation.transport.ordersOnRoute,
      sortable: true,
    },
    {
      label: translation.transport.status,
      key: "status",
      filterable: true,
      minWidth: "130px",
      renderer: (route) => (
        <Tooltip
          title={
            route.status === "calculating"
              ? "Root is calculating the impact of this route. All you have to do is wait. ⏳"
              : route.status === "incomplete"
                ? "Some of the legs in this route are either incomplete or have errors. Click to investigate. 🧐"
                : undefined
          }
        >
          <>
            <StatusTag status={route.status} />
          </>
        </Tooltip>
      ),
    },
    {
      key: "actions",
      label: translation.transport.actions,
      minWidth: "80px",
      maxWidth: "80px",
      noLink: true,
      renderer: (route) => (
        <ActionsPopover
          zIndex={4}
          actions={
            <>
              <InboundRouteWizard
                departureId={route.departure_id}
                arrivalId={route.arrival_id}
                transportCategory={route.transport_category}
                year={year}
                placement="left"
              >
                <ActionsPopOverItem icon={<BranchesOutlined />}>Edit</ActionsPopOverItem>
              </InboundRouteWizard>
              <ExportInboundTransportAction
                route={{
                  departure_facility_id: route.departure_id,
                  arrival_facility_id: route.arrival_id,
                  transport_category: route.transport_category,
                }}
                year={year}
                placement="left"
              >
                <ActionsPopOverItem
                  icon={<ExportOutlined />}
                  black
                >
                  Export
                </ActionsPopOverItem>
              </ExportInboundTransportAction>
              <DeleteInboundTransportAction
                route={{
                  departure_facility_id: route.departure_id,
                  arrival_facility_id: route.arrival_id,
                  transport_category: route.transport_category,
                }}
                year={year}
              >
                <ActionsPopOverItem
                  icon={<DeleteOutlined />}
                  danger
                >
                  Delete
                </ActionsPopOverItem>
              </DeleteInboundTransportAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const years = useInboundTransportYearStatus();

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
    });

  const data = useGetInboundTransport({
    year,
    pagination,
    filter,
    search,
    sort,
  });

  const totals = useGetInboundTransportTotals({
    year,
    filter,
    search,
  });

  return (
    <div>
      <PageHeader
        title="Inbound transport"
        description={translation.transport.outboundIntro}
        right={<InboundTransportButtonGroup />}
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={years.data}
                isLoading={years.isLoading}
                onChange={() => setPage(1)}
              />
              <PageButtonGroup>
                <ExportInboundTransportAction year={year}>
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportInboundTransportAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  actions={
                    <>
                      <DeleteInboundTransportAction
                        year={year}
                        onSuccess={() => setIsMoreOpen(false)}
                      >
                        <ActionsPopOverItem
                          icon={<DeleteOutlined />}
                          danger
                        >
                          Delete
                        </ActionsPopOverItem>
                      </DeleteInboundTransportAction>
                    </>
                  }
                />
              </PageButtonGroup>
            </>
          }
          right={
            <StatusTags
              data={totals.data}
              loading={totals.isLoading || totals.isFetching}
              calculatingTooltip="Root is calculating the impact of some transport legs in the background. All you have to do is wait ⏳"
              errorTooltip="Root has not been able to calculate the impact of some legs. Click a route with errors to see what's going on. 🧐"
            />
          }
        />
        <Table
          columns={columns}
          availableFilters={transportUploadFilters}
          rowKey="id"
          rowLink={(record) => rowLink(record)}
          onSearchFilterSortPaginate={onSearchFilterSortPaginate}
          loading={data.isLoading || totals.isFetching}
          data={data.data?.data}
          totalRows={totals.data?.total_count}
          pagination={pagination}
          sort={sort}
          filter={filter}
          search={search}
          emptyState={{ noResultsMessage: `No result for ${year}.` }}
        />
      </div>
    </div>
  );
}
